import {Component, EventEmitter, Output, inject} from '@angular/core';
import {NavigationEnd, Router, RouterModule} from '@angular/router';
import {AuthenticationService} from '@auth/authentication.service';
import {NotificationService} from '@notifications';
import {LocalStorageService} from '@services/local-storage.service';
import {CurrentUserModel} from '@models/current-user.model';
import {CustomerService} from '@common/services/customer-service';
import {DomSanitizer, SafeResourceUrl} from '@angular/platform-browser';
import {UserGuideService} from '@services/user-guide.service';
import {BsModalService} from 'ngx-bootstrap/modal';
import {NewBankRequestComponent} from '../../../new-bank-request/new-bank-request';
import {TranslateModule, TranslateService} from '@ngx-translate/core';
import {CUSTOMER, CUSTOMER_RELATED, CUSTOMER_RELATED_NO_FIRM, EMPLOYEE_CLIENT_ASSIGN} from '@auth/constants';
import {filter} from 'rxjs';
import {LIST_OF_BANKS} from '../constants';
import {CommonModule} from '@angular/common';
import {toSignal} from '@angular/core/rxjs-interop';
import {CollapseModule} from 'ngx-bootstrap/collapse';
import {BsDropdownModule} from 'ngx-bootstrap/dropdown';
import {ReportBugComponent} from '../../../report-bug/report-bug.component';
import {ChatbotComponent} from '../../../chatbot/chatbot.component';
import {LanguageSwitcher} from '@reusable/language-switcher/language-switcher';
import {UserGuideDirective} from '../../../user-guide/user-guide.directive';
import {USER_GUIDE_I18N} from '../../../user-guide/guide-i18n.constants';
import {TaskCenterRealtimeService} from '../../../pages/task-center/task-center-realtime.service';

interface IMenuLink {
  id: string;
  title: string;
  route?: string;
  url?: string;
  beta?: boolean;
  visible?: boolean;
  children?: IMenuLink[];
  guide?: {
    guideId: string;
    guideTitle: string;
    guideText: string;
    guideButtonCaption: string;
    guideStep: number;
  }
}

@Component({
  selector: 'app-header-component',
  templateUrl: './header.component.html',
  styleUrls: ['header.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    TranslateModule,
    CollapseModule,
    BsDropdownModule,
    ReportBugComponent,
    ChatbotComponent,
    LanguageSwitcher,
    UserGuideDirective
  ]
})
export class HeaderComponent {
  private readonly taskCenterRealtimeService = inject(TaskCenterRealtimeService);
  currentUser: CurrentUserModel;
  currentUserIsCustomer: boolean;
  currentUserIsEmployee: boolean;
  currentUserIsCustomerButNotFirm: boolean;
  // an employee who assigns clients to the team gets the Employees page too
  employeesAvailable: boolean;
  firmsAvailable: boolean;
  currentUserName: string = '';
  logo: SafeResourceUrl;
  readonly taskCenterEventCount = toSignal(this.taskCenterRealtimeService.taskCenterEventCount$, {initialValue: 0});

  // todo header shouldn't know about sidebar
  isCollapsed: boolean = true;
  banksListUrl: string = LIST_OF_BANKS;
  links: IMenuLink[];
  readonly guideKeys = USER_GUIDE_I18N;

  @Output() onChangedSidebar: EventEmitter<any> = new EventEmitter<any>();

  constructor(
    private authService: AuthenticationService,
    private router: Router,
    private localStorageService: LocalStorageService,
    private notificationService: NotificationService,
    private customerService: CustomerService,
    private sanitizer: DomSanitizer,
    private userGuideService: UserGuideService,
    private bsModalService: BsModalService,
    private translateService: TranslateService,
  ) {
    // collapse navbar on navigation
    this.router.events
    .pipe(filter((e) => e instanceof NavigationEnd))
    .subscribe((e) => this.isCollapsed = true);

    this.currentUser = this.localStorageService.getCurrentUserDetails();
    this.currentUserIsCustomer = CUSTOMER_RELATED.includes(this.currentUser.role);
    this.currentUserIsEmployee = this.currentUser.role === 'EMPLOYEE' || this.currentUser.role === 'FIRM_EMPLOYEE';
    this.currentUserIsCustomerButNotFirm = CUSTOMER_RELATED_NO_FIRM.includes(this.currentUser.role);
    this.employeesAvailable = this.currentUserIsCustomer || this.localStorageService.hasZone([EMPLOYEE_CLIENT_ASSIGN]);
    this.firmsAvailable = this.currentUserIsCustomerButNotFirm && !this.currentUser.isGroupsEnabled;
    this.currentUserName = (this.currentUser && this.currentUser.fullName) ? this.currentUser.fullName : '';

    const toolsLinks = [
      {
        id: 'document-storage',
        title: translateService.instant('Doc. storage'),
        route: '/document-storage',
      },
      {
        id: 'cloud-storage',
        title: translateService.instant('Cloud Storage'),
        route: `/cloud-storage`,
      },
      {
        id: 'notes',
        title: translateService.instant('Notes'),
        route: '/notes',
      },
      {
        id: 'pdf-converter',
        title: translateService.instant('PDF Converter'),
        route: '/pdf-converter',
      },
      {
        id: 'ls-bank-reconciliation',
        title: translateService.instant('LS Bank Reconciliation'),
        route: '/ls-created-statements',
      },
      {
        id: 'supported-institutions',
        title: translateService.instant('Supported Institutions'),
        route: '/supported-institutions',
      },
      {
        id: 'quickbooks-transactions',
        title: translateService.instant('QuickBooks Transactions'),
        route: '/quickbooks-transactions',
      },
    ];

    const crmLinks = [
      {
        id: 'client-list',
        title: translateService.instant('Client List'),
        route: '/crm/client-list',
      },
      {
        id: 'communication-center',
        title: translateService.instant('Communication Center'),
        route: '/crm/communication-center'
      },
    ];

    if (this.currentUserIsCustomer) {
      toolsLinks.unshift(
        {
          id: 'employees',
          title: translateService.instant('Add Employee'),
          route: '/employees',
        },
      );
    }

    if (this.currentUserIsCustomerButNotFirm) {
      toolsLinks.unshift(
        {
          id: 'customer-admin',
          title: translateService.instant('Add Administrator'),
          route: '/customer-admin',
        },
      );

      // firms available only for outsource companies
      if (!this.currentUser.isGroupsEnabled) {
        toolsLinks.unshift({
          id: 'firms',
          title: translateService.instant('Add Firm'),
          route: '/firms',
        });
      }
    }

    if (CUSTOMER_RELATED_NO_FIRM.includes(this.currentUser.role) && this.currentUser.isGroupsEnabled) {
      crmLinks.push(
        {
          id: 'client-groups',
          title: translateService.instant('Client Grouping'),
          route: '/crm/client-groups'
        },
      );
    }

    this.links = [
      {
        id: 'home',
        title: translateService.instant('Dashboard'),
        route: '/',
      },
      {
        id: 'crm',
        title: translateService.instant('Client Actions'),
        route: '/crm',
        guide: {
          guideId: 'ADD_CLIENT',
          guideTitle: this.guideKeys.addClient.title,
          guideText: this.guideKeys.addClient.text1,
          guideButtonCaption: 'NAVIGATE',
          guideStep: 1,
        },
        children: crmLinks,
      },
      {
        id: 'summary-tabs',
        title: translateService.instant('Bank Connections'),
        children: [
          {
            id: 'summary',
            title: translateService.instant('Summary'),
            route: '/banks',
          },
          {
            id: 'transactions',
            title: translateService.instant('Transactions'),
            route: '/banks/transactions',
          },
          {
            id: 'transactions-verify',
            title: translateService.instant('Txs Verify'),
            route: '/banks/transactions-verify',
          },
          {
            id: 'running-balance',
            title: translateService.instant('Running Balance'),
            route: '/banks/running-balance',
          },
          {
            id: 'statements',
            title: translateService.instant('Statements'),
            route: '/banks/statements',
          },
          {
            id: 'checks',
            title: translateService.instant('Checks'),
            route: '/banks/checks',
          },
        ]
      },
      {
        id: 'task-center',
        title: translateService.instant('Task Center'),
        route: '/task-center',
      },
      {
        id: 'rules-engine',
        title: 'Rules Engine',
        children: [
          {
            id: 'quickbooks',
            title: translateService.instant('QuickBooks'),
            route: '/quickbooks',
          },
          {
            id: 'zohobooks',
            title: translateService.instant('ZohoBooks'),
            route: '/zohobooks',
          },
          {
            id: 'manual',
            title: translateService.instant('Manual'),
            route: '/manual',
          },
        ]
        // beta: true,
      },
      {
        id: 'quickbooks-close',
        title: translateService.instant('LS QBO Validation'),
        route: '/quickbooks-close',
      },
      {
        id: 'tools',
        title: translateService.instant('Tools'),
        children: toolsLinks,
      },
    ];

    this.customerService.logo.subscribe(({logoUrl, ...colors}) => {
      if (logoUrl) {
        this.sanitizeLogo(logoUrl);
      } else {
        this.logo = 'assets/images/new-logo.png';
      }

      if (colors && Object.keys(colors).length) {
        this.setupColors(colors);
      }
    });

    this.navigateToClients = this.navigateToClients.bind(this);
  }

  toggleWrapper() {
    this.isCollapsed = !this.isCollapsed;
  }

  setupColors(colors) {
    document.documentElement.style.setProperty('--base-green', colors.mainColor);
    document.documentElement.style.setProperty('--base-white', colors.bgColor);
    document.documentElement.style.setProperty('--base-blue', colors.firstAddColor);
    document.documentElement.style.setProperty('--content-bg', colors.secondAddColor);
  }

  sanitizeLogo(logo) {
    this.logo = this.sanitizer.bypassSecurityTrustResourceUrl(logo);
  }

  /**
   * logout current user
   */
  logout() {
    this.authService.logout();
  }

  navigateToClients() {
    this.router.navigate(['/crm/client-list'])
    .then(() => {
      setTimeout(() => this.userGuideService.initStep(1), 500);
    });
  }

  requestNewBank() {
    this.bsModalService.show(NewBankRequestComponent, {
      ignoreBackdropClick: true,
    });
  }

  downloadFdeApp() {
    window.open(
      this.customerService.getFdeAppUrl(navigator.userAgent.toLowerCase().includes('win') ? 'windows' : 'macos')
    );
  }

  protected readonly CUSTOMER = CUSTOMER;
}
